import { yupResolver } from '@hookform/resolvers/yup';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InputText from 'src/components/InputText/InputText';
import InputTextArea from 'src/components/InputTextArea';
import PhoneInput from 'src/components/PhoneInput/PhoneInput';
import { BOL_LIMIT_BY_CARRIER, REGULAR_EXPRESSION_NEW } from 'src/constants/common';
import { usePhone } from 'src/hooks/usePhone';
import { selectCommonCarrierRequiredFields } from 'src/redux/address.slice';
import { selectFormData, setQuoteBookingFormData } from 'src/redux/bookingStepper.slice';
import * as Yup from 'yup';

import { getBolVisibleFields, groupFieldsBySection, IFieldRegistryEntry, resolveBolFieldValue } from './fieldRegistry.config';

interface IBolRequirementsProps {
    stepperFooterRef?: React.RefObject<any>;
}

const EMAIL_FIELDS = new Set(['shipperEmail', 'consigneeEmail']);
const PHONE_FIELDS = new Set(['shipperContactNumber', 'consigneeContactNumber']);

const BolRequirements = ({ stepperFooterRef }: IBolRequirementsProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const formData = useSelector(selectFormData);
    const commonCarrierRequiredFields = useSelector(selectCommonCarrierRequiredFields);
    const { isPhoneValid } = usePhone();

    const carrierSlug: string | null = formData?.carrierSlug ?? null;
    const bolUserEditedFields: string[] = formData?.bolUserEditedFields ?? [];
    const bolLimit = carrierSlug ? (BOL_LIMIT_BY_CARRIER[carrierSlug.toUpperCase()] as number | undefined) : undefined;

    const visibleFields = useMemo(() => getBolVisibleFields(commonCarrierRequiredFields, carrierSlug), [commonCarrierRequiredFields, carrierSlug]);
    const groupedFields = useMemo(() => groupFieldsBySection(visibleFields), [visibleFields]);

    const markFieldAsUserEdited = useCallback(
        (fieldKey: string) => {
            if (!bolUserEditedFields.includes(fieldKey)) {
                dispatch(setQuoteBookingFormData({ bolUserEditedFields: [...bolUserEditedFields, fieldKey] }));
            }
        },
        [bolUserEditedFields, dispatch]
    );

    // Shared with the stepper's pre-booking gate (see fieldRegistry.config.ts) so both judge the same
    // value for a field the user has not touched yet.
    const getDefaultValue = useCallback((fieldKey: string): string => resolveBolFieldValue(fieldKey, formData), [formData]);

    const defaultValues = useMemo(() => {
        const values: Record<string, string> = {};
        visibleFields.forEach((field) => {
            values[field.key] = getDefaultValue(field.key);
        });

        return values;
    }, [visibleFields, getDefaultValue]);

    const validationSchema = useMemo(() => {
        const shape: Record<string, any> = {};

        visibleFields.forEach((field) => {
            if (EMAIL_FIELDS.has(field.key)) {
                shape[field.key] = Yup.string()
                    .required(t('BookOrder.Booking_Stepper.Bol_Requirements.Validation.Email_Required'))
                    .matches(REGULAR_EXPRESSION_NEW.EMAIL, t('BookOrder.Booking_Stepper.Bol_Requirements.Validation.Email_Invalid'))
                    .required(t('BookOrder.Booking_Stepper.Bol_Requirements.Validation.Email_Required'));
            } else if (PHONE_FIELDS.has(field.key)) {
                shape[field.key] = Yup.string()
                    .trim()
                    .required(t('BookOrder.Booking_Stepper.Bol_Requirements.Validation.Phone_Required'))
                    .test('is-valid-phone-number', t('BookOrder.Booking_Stepper.Bol_Requirements.Validation.Phone_Invalid'), (value) => {
                        if (!value) return true;
                        if (!/^\+\d{1,4}\d+$/.test(value)) return false;
                        const phoneNumber = parsePhoneNumberFromString(value);
                        if (!phoneNumber || !phoneNumber.country) return false;

                        return isPhoneValid(phoneNumber.nationalNumber, phoneNumber.country);
                    });
            } else if (field.key === 'specialNotes') {
                // BOL Instructions is optional (client decision) — only the carrier char limit applies.
                shape[field.key] = Yup.string()
                    .trim()
                    .test('bol-char-limit', `Exceeds character limit`, (value) => {
                        if (!bolLimit || !value) return true;

                        return value.length <= bolLimit;
                    });
            } else {
                shape[field.key] = Yup.string().trim().required(t('BookOrder.Booking_Stepper.Bol_Requirements.Validation.Field_Required'));
            }
        });

        return Yup.object().shape(shape);
    }, [visibleFields, bolLimit, isPhoneValid, t]);

    const {
        control,
        getValues,
        reset,
        trigger,
        clearErrors,
        formState: { errors },
    } = useForm<Record<string, string>>({
        defaultValues,
        resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
        const newValues: Record<string, string> = {};
        visibleFields.forEach((field) => {
            newValues[field.key] = getDefaultValue(field.key);
        });
        reset(newValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carrierSlug, visibleFields]);

    const syncToRedux = useCallback(() => {
        dispatch(setQuoteBookingFormData({ bolRequirementsData: getValues() }));
    }, [getValues, dispatch]);

    const handleNext = useCallback(async () => {
        const isValid = await trigger();
        if (!isValid) throw new Error('Validation failed');
        syncToRedux();
    }, [trigger, syncToRedux]);

    const handleBack = useCallback(() => {
        syncToRedux();
    }, [syncToRedux]);

    useEffect(() => {
        if (stepperFooterRef && 'current' in stepperFooterRef) {
            // triggerValidation is what the parent calls after sending the user back here from the
            // booking gate. Without it the bounce landed on a step with no errors shown, so nothing
            // told the user which field was missing.
            (stepperFooterRef as any).current = { triggerNext: handleNext, triggerBack: handleBack, triggerValidation: () => trigger() };
        }
    }, [stepperFooterRef, handleNext, handleBack, trigger]);

    const renderField = (field: IFieldRegistryEntry & { key: string }) => (
        <div key={field.key} className="stepper-content-container-wrap">
            <div className="stepper-content-container">
                <div className="stepper-lable-wrap">
                    <label className="stepper-label-common flex">
                        {field.label}
                        {field.isRequired !== false && <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>}
                    </label>
                </div>
                <div className="flex-1 min-w-0">
                    <Controller
                        name={field.key}
                        control={control}
                        render={({ field: formField }) => {
                            if (field.key === 'specialNotes') {
                                const charCount = (formField.value || '').length;
                                const isOverLimit = bolLimit ? charCount > bolLimit : false;

                                return (
                                    <>
                                        <InputTextArea
                                            inputName="specialNotes"
                                            value={formField.value || ''}
                                            onChangeFunc={(e: any) => {
                                                formField.onChange(e.target.value);
                                                clearErrors('specialNotes' as any);
                                                markFieldAsUserEdited(field.key);
                                            }}
                                            placeholder={field.placeholder || 'Enter BOL instructions'}
                                            maxLength={400}
                                            rows={5}
                                            errorText={!!errors?.specialNotes}
                                        />
                                        {bolLimit && (
                                            <div className={`text-xs text-right mt-1 font-medium ${isOverLimit ? 'text-red-500' : 'text-gray500'}`}>
                                                {charCount} / {bolLimit}
                                            </div>
                                        )}
                                    </>
                                );
                            }

                            if (PHONE_FIELDS.has(field.key)) {
                                return (
                                    <PhoneInput
                                        name={field.key}
                                        value={formField.value || ''}
                                        onNumberChange={(_country: any, number: any, code: any) => {
                                            const newValue = number ? `+${code}${number}` : '';
                                            // Only mark as user-edited when the value actually changes —
                                            // PhoneInput fires onNumberChange on mount with the pre-filled value,
                                            // so comparing against current form value avoids false positives.
                                            if (newValue !== (formField.value || '')) markFieldAsUserEdited(field.key);
                                            formField.onChange(newValue);
                                            clearErrors(field.key as any);
                                        }}
                                        errors={!!errors?.[field.key]}
                                        isShowErrorMsg={false}
                                    />
                                );
                            }

                            return (
                                <InputText
                                    inputName={field.key}
                                    value={formField.value || ''}
                                    onChangeFunc={(e: any) => {
                                        formField.onChange(e.target.value);
                                        clearErrors(field.key as any);
                                        markFieldAsUserEdited(field.key);
                                    }}
                                    placeholder={field.placeholder}
                                    errorText={!!errors?.[field.key]}
                                    inputType={EMAIL_FIELDS.has(field.key) ? 'email' : 'text'}
                                    maxLength={['shipperContactName', 'consigneeContactName'].includes(field.key) ? 50 : 200}
                                />
                            );
                        }}
                    />
                </div>
            </div>
        </div>
    );

    const sectionOrder: ('shipper' | 'consignee' | 'orderDetails')[] = ['shipper', 'consignee', 'orderDetails'];

    return (
        <div className="!pt-0">
            {sectionOrder.map((section) => {
                const fields = groupedFields[section];
                if (!fields || fields.length === 0) return null;

                return <React.Fragment key={section}>{fields.map(renderField)}</React.Fragment>;
            })}
        </div>
    );
};

export default BolRequirements;
